import { BrokerIdentityPill } from '@/components/Broker/BrokerIdentityPill'
import { BrokerNotePopover } from '@/components/Broker/BrokerNotePopover'
import { BrokerNoteSheet } from '@/components/Broker/BrokerNoteSheet'
import { cn } from '@/lib/utils'

export interface MatchedBroker {
  name: string
  phone: string
  email: string
  /** Headshot image URL — falls back to initials when omitted. */
  avatarUrl?: string
  /** Dialable number for the `tel:` link, when the display phone isn't one. */
  phoneE164?: string
}

/**
 * Right-side nav cluster for pages built for an already-matched customer
 * (e.g. the Quotes page) — broker identity plus quick contact, in place of the
 * default phone/login utility links.
 *
 * Desktop-only — below `md` it's hidden and {@link MatchedBrokerMobileBar}
 * takes over as a full-width row beneath the nav. Page-level actions belong in
 * `ProductFlowNav`'s `rightAction`, not here.
 */
export function MatchedBrokerNavCluster({
  broker,
  allowNote,
}: {
  broker: MatchedBroker
  allowNote?: boolean
}) {
  return (
    <BrokerIdentityPill
      name={broker.name}
      phone={broker.phone}
      phoneE164={broker.phoneE164}
      email={broker.email}
      avatarUrl={broker.avatarUrl}
      action={allowNote ? <BrokerNotePopover brokerName={broker.name} /> : undefined}
      className="max-md:hidden"
    />
  )
}

/**
 * Mobile counterpart to {@link MatchedBrokerNavCluster} — a full-width row
 * rendered just beneath the nav on narrow viewports (hidden at `md`+): the same
 * pill, stretched, with an icon-only call button.
 */
export function MatchedBrokerMobileBar({
  broker,
  className,
  allowNote,
}: {
  broker: MatchedBroker
  className?: string
  allowNote?: boolean
}) {
  return (
    <div className={cn('border-ink-100 bg-ink-50 w-full border-b px-2.75 py-2 md:hidden', className)}>
      <BrokerIdentityPill
        name={broker.name}
        phone={broker.phone}
        phoneE164={broker.phoneE164}
        email={broker.email}
        avatarUrl={broker.avatarUrl}
        hidePhoneNumber
        action={allowNote ? <BrokerNoteSheet brokerName={broker.name} /> : undefined}
        className="w-full justify-between"
      />
    </div>
  )
}
